/**
 * CLI Seed Workflow Creation
 *
 * Creates a seed workflow shell with outputs and an optional paired teardown.
 * Supports flag-based and JSON-based input.
 *
 * @module workflow-seed-create
 */

import fs from 'node:fs/promises';
import process from 'node:process';
import { getArgValue, hasFlag, resolveAppUrl } from './auth.js';
import { apiRequest } from './cli-helpers.js';

/* ── Types ────────────────────────────────────────────────────────────── */

type SeedOutput = {
  dataKey: string;
  name: string;
  displayLabel: string;
  description?: string;
  credentialOutput?: Record<string, unknown>;
};

type SeedCreateInput = {
  name: string;
  description?: string;
  status?: 'draft' | 'approved';
  outputs?: SeedOutput[];
};

type WorkflowResponse = {
  ok: boolean;
  error?: string;
  workflow?: { id: string; name: string; status: string; flowType: string };
};

type WorkflowUpdateResponse = {
  ok: boolean;
  error?: string;
  workflow?: { id: string };
};

/* ── Helpers ──────────────────────────────────────────────────────────── */

function getAllArgValues(argv: string[], flag: string): string[] {
  const values: string[] = [];
  for (let i = 0; i < argv.length; i++) {
    if (argv[i] === flag && i + 1 < argv.length) {
      values.push(argv[i + 1]!);
      i++;
    }
  }
  return values;
}

/**
 * Parse an --output flag value: "dataKey:Name[:description]"
 */
function parseOutputFlag(value: string): SeedOutput {
  const parts = value.split(':');
  if (parts.length < 2) {
    throw new Error(
      `Invalid --output format: "${value}". Expected "dataKey:Name" or "dataKey:Name:Description".`
    );
  }
  const [dataKey, name, ...descParts] = parts;
  const description = descParts.length > 0 ? descParts.join(':') : undefined;
  return {
    dataKey: dataKey!.trim(),
    name: name!.trim(),
    displayLabel: name!.trim(),
    description: description?.trim() || undefined,
  };
}

async function readFromStdin(): Promise<string> {
  const chunks: Buffer[] = [];
  const stdin = process.stdin;

  if (stdin.isTTY) {
    throw new Error('No input on stdin. Pipe JSON or use --from-file.');
  }

  return new Promise((resolve, reject) => {
    stdin.on('data', (chunk) => {
      chunks.push(typeof chunk === 'string' ? Buffer.from(chunk) : chunk);
    });
    stdin.on('end', () => resolve(Buffer.concat(chunks).toString('utf-8')));
    stdin.on('error', reject);
  });
}

async function readJsonInput(argv: string[]): Promise<SeedCreateInput> {
  let raw: string;

  const fromFile = getArgValue(argv, '--from-file');
  if (fromFile) {
    try {
      raw = await fs.readFile(fromFile, 'utf-8');
    } catch (err) {
      throw new Error(`Failed to read file ${fromFile}: ${err instanceof Error ? err.message : String(err)}`);
    }
  } else {
    raw = await readFromStdin();
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error('Invalid JSON input.');
  }

  const input = parsed as SeedCreateInput;
  if (!input.name || typeof input.name !== 'string') {
    throw new Error('JSON input must include a "name" string field.');
  }

  // Normalize outputs: default displayLabel to name if missing
  if (Array.isArray(input.outputs)) {
    for (const out of input.outputs) {
      if (!out.dataKey || !out.name) {
        throw new Error('Each output must have "dataKey" and "name" fields.');
      }
      if (!out.displayLabel) {
        out.displayLabel = out.name;
      }
    }
  }

  return input;
}

function resolveInput(argv: string[]): Promise<SeedCreateInput> | SeedCreateInput {
  const fromFile = getArgValue(argv, '--from-file');
  const fromStdin = hasFlag(argv, '--from-stdin');

  // JSON input mode
  if (fromFile || fromStdin) {
    return readJsonInput(argv);
  }

  // Flag-based input mode
  const name = getArgValue(argv, '--name');
  if (!name) {
    console.error('Error: --name is required (or use --from-stdin / --from-file for JSON input).');
    console.error('Usage: canary workflow seed create --name <name> [--output key:name[:desc]] ...');
    process.exit(1);
  }

  const description = getArgValue(argv, '--description');
  const status = getArgValue(argv, '--status') as 'draft' | 'approved' | undefined;

  // Parse --output flags
  const outputFlags = getAllArgValues(argv, '--output');
  const outputs: SeedOutput[] = outputFlags.map(parseOutputFlag);

  // Parse --outputs-json for complex outputs (credential configs etc.)
  const outputsJsonRaw = getArgValue(argv, '--outputs-json');
  if (outputsJsonRaw) {
    try {
      const parsed = JSON.parse(outputsJsonRaw) as SeedOutput[];
      if (!Array.isArray(parsed)) throw new Error('not an array');
      for (const out of parsed) {
        if (!out.dataKey || !out.name) {
          throw new Error('Each output must have "dataKey" and "name" fields.');
        }
        if (!out.displayLabel) out.displayLabel = out.name;
      }
      outputs.push(...parsed);
    } catch (err) {
      console.error(`Error: Invalid --outputs-json: ${err instanceof Error ? err.message : String(err)}`);
      process.exit(1);
    }
  }

  return { name, description: description || undefined, status, outputs };
}

/* ── Main Handler ────────────────────────────────────────────────────── */

export async function handleWorkflowSeedCreate(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  if (hasFlag(argv, '--help', '-h')) {
    printSeedCreateHelp();
    return;
  }

  const jsonOutput = hasFlag(argv, '--json');
  const noTeardown = hasFlag(argv, '--no-teardown');

  let input: SeedCreateInput;
  try {
    input = await resolveInput(argv);
  } catch (err) {
    console.error(`Error: ${err instanceof Error ? err.message : String(err)}`);
    process.exit(1);
  }

  // 1. Create seed workflow
  const seedResult = await apiRequest<WorkflowResponse>(
    apiUrl,
    token,
    'POST',
    '/workflows',
    {
      name: input.name,
      description: input.description,
      status: input.status ?? 'draft',
      flowType: 'seed',
      createdVia: 'cli',
    }
  );

  // Success = a workflow row came back. `POST /workflows` historically omitted
  // `ok` from its envelope, so gating on `ok` alone reported false failures even
  // though the seed was created; presence of `workflow` is the reliable signal.
  if (!seedResult.workflow) {
    if (jsonOutput) {
      console.log(JSON.stringify({ ok: false, error: seedResult.error ?? 'Failed to create seed workflow' }, null, 2));
    } else {
      console.error(`Error: ${seedResult.error ?? 'Failed to create seed workflow'}`);
    }
    process.exit(1);
  }

  const seedId = seedResult.workflow.id;
  const appUrl = resolveAppUrl(apiUrl);

  // 2. Set seed outputs if provided
  let outputsSaved = false;
  if (input.outputs && input.outputs.length > 0) {
    const outputsResult = await apiRequest<WorkflowUpdateResponse>(
      apiUrl,
      token,
      'PUT',
      `/workflows/${seedId}`,
      { seedOutputsJson: input.outputs }
    );

    if (outputsResult.ok || outputsResult.workflow) {
      outputsSaved = true;
    } else {
      console.warn(`Warning: Seed created but failed to save outputs: ${outputsResult.error ?? 'Unknown error'}`);
    }
  }

  // 3. Create paired teardown (unless --no-teardown)
  let teardownId: string | undefined;
  let teardownName: string | undefined;
  if (!noTeardown) {
    const tdName = `Teardown: ${input.name}`;
    const tdResult = await apiRequest<WorkflowResponse>(
      apiUrl,
      token,
      'POST',
      '/workflows',
      {
        name: tdName,
        description: `Cleans up seed data from "${input.name}"`,
        status: 'draft',
        flowType: 'teardown',
        createdVia: 'cli',
      }
    );

    if (tdResult.workflow) {
      teardownId = tdResult.workflow.id;
      teardownName = tdName;

      // 4. Link teardown to seed
      const linkResult = await apiRequest<WorkflowUpdateResponse>(
        apiUrl,
        token,
        'PUT',
        `/workflows/${seedId}`,
        { teardownWorkflowId: teardownId }
      );

      if (!linkResult.ok && !linkResult.workflow) {
        console.warn(`Warning: Teardown created but failed to link to seed: ${linkResult.error ?? 'Unknown error'}`);
      }
    } else {
      console.warn(`Warning: Failed to create paired teardown: ${tdResult.error ?? 'Unknown error'}`);
    }
  }

  // 5. Output results
  if (jsonOutput) {
    const result: Record<string, unknown> = {
      ok: true,
      seed: {
        id: seedId,
        name: input.name,
        status: input.status ?? 'draft',
        flowType: 'seed',
        outputs: outputsSaved ? input.outputs : [],
        url: `${appUrl}/flows/${seedId}`,
      },
    };
    if (teardownId) {
      result.teardown = {
        id: teardownId,
        name: teardownName,
        status: 'draft',
        flowType: 'teardown',
        url: `${appUrl}/flows/${teardownId}`,
      };
    }
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  console.log(`\n\u2713 Seed workflow created: ${input.name}`);
  console.log(`  ID: ${seedId}`);
  console.log(`  Status: ${input.status ?? 'draft'}`);
  if (input.outputs && input.outputs.length > 0) {
    const keys = input.outputs.map((o) => o.dataKey).join(', ');
    console.log(`  Outputs: ${keys}${!outputsSaved ? ' (save failed)' : ''}`);
  }
  console.log(`  URL: ${appUrl}/flows/${seedId}`);

  if (teardownId) {
    console.log(`\n\u2713 Teardown workflow created: ${teardownName}`);
    console.log(`  ID: ${teardownId}`);
    console.log(`  URL: ${appUrl}/flows/${teardownId}`);
  }
}

function printSeedCreateHelp(): void {
  console.log(
    [
      'Usage: canary workflow seed create [options]',
      '',
      'Create a seed workflow with declared outputs and an optional paired teardown.',
      '',
      'Flag-based input:',
      '  --name <name>              Seed workflow name (required)',
      '  --description <desc>       Description',
      '  --output <key:name[:desc]> Declare an output (repeatable)',
      '  --outputs-json <json>      Full outputs array as inline JSON (for credential configs)',
      '  --no-teardown              Skip creating paired teardown workflow',
      '  --status <status>          Initial status (default: draft)',
      '  --json                     Output raw JSON',
      '',
      'JSON input:',
      '  --from-stdin               Read full config from stdin',
      '  --from-file <path>         Read full config from file',
      '',
      'Examples:',
      '  canary workflow seed create --name "Create Project" --output "project_id:Project ID"',
      '',
      '  canary workflow seed create --from-stdin <<\'EOF\'',
      '  {',
      '    "name": "Create Test User",',
      '    "outputs": [{',
      '      "dataKey": "user_cred",',
      '      "name": "Test User Credential",',
      '      "credentialOutput": {',
      '        "fieldMapping": { "username": "$generate.email", "password": "Test123!" },',
      '        "emailPrefix": "testuser",',
      '        "createInboxRecipient": true',
      '      }',
      '    }]',
      '  }',
      '  EOF',
    ].join('\n')
  );
}
